import { formatManwon } from '../../utils/formatKRW';

export type IncomeLevel = 'below55m' | 'above55m';

export interface TaxBenefitFormValues {
  irpContribution: number;
  isaAnnualContribution: number;
  isaType: 'general' | 'youth';
  expectedIsaProfit: number;
  incomeLevel: IncomeLevel;
  projectionYears: number;
}

export interface TaxBenefitYearDataPoint {
  year: number;
  irpCumulative: number;
  isaCumulative: number;
  total: number;
}

export interface TaxBenefitResult {
  irpAnnualBenefit: number;
  irpEffectiveContribution: number;
  isaAnnualTaxSaving: number;
  annualTotalBenefit: number;
  cumulativeBenefit: number;
  irpTaxCreditRate: number;
  isaNonTaxableLimit: number;
  yearlyData: TaxBenefitYearDataPoint[];
}

const IRP_MAX_CONTRIBUTION = 9_000_000;
const ISA_NONTAXABLE_GENERAL = 2_000_000;
const ISA_NONTAXABLE_YOUTH = 4_000_000;
const DIVIDEND_TAX_RATE = 0.154;

export function calculateTaxBenefit(values: TaxBenefitFormValues): TaxBenefitResult {
  const { irpContribution, isaType, expectedIsaProfit, incomeLevel, projectionYears } = values;

  const irpTaxCreditRate = incomeLevel === 'below55m' ? 0.165 : 0.132;
  const irpEffectiveContribution = Math.min(irpContribution, IRP_MAX_CONTRIBUTION);
  const irpAnnualBenefit = Math.round(irpEffectiveContribution * irpTaxCreditRate);

  const isaNonTaxableLimit = isaType === 'youth' ? ISA_NONTAXABLE_YOUTH : ISA_NONTAXABLE_GENERAL;
  const isaTaxSavedTotal = Math.round(
    Math.min(expectedIsaProfit, isaNonTaxableLimit) * DIVIDEND_TAX_RATE
  );
  const isaAnnualTaxSaving = Math.round(isaTaxSavedTotal / 5);

  const annualTotalBenefit = irpAnnualBenefit + isaAnnualTaxSaving;
  const cumulativeBenefit = annualTotalBenefit * projectionYears;

  const yearlyData = Array.from({ length: projectionYears }, (_, i) => {
    const year = i + 1;
    return {
      year,
      irpCumulative: irpAnnualBenefit * year,
      isaCumulative: isaAnnualTaxSaving * year,
      total: annualTotalBenefit * year,
    };
  });

  return {
    irpAnnualBenefit,
    irpEffectiveContribution,
    isaAnnualTaxSaving,
    annualTotalBenefit,
    cumulativeBenefit,
    irpTaxCreditRate,
    isaNonTaxableLimit,
    yearlyData,
  };
}

export function getTaxBenefitCallout(result: TaxBenefitResult, values: TaxBenefitFormValues) {
  if (values.irpContribution < IRP_MAX_CONTRIBUTION) {
    const unused = IRP_MAX_CONTRIBUTION - values.irpContribution;
    const additionalBenefit = Math.round(unused * result.irpTaxCreditRate);
    return {
      title: 'IRP 납입 한도를 아직 다 채우지 않았습니다',
      body: `한도(연 900만 원)까지 채우면 ${formatManwon(additionalBenefit)}을 추가로 환급받을 수 있습니다. 가장 확실한 수익률이 보장되는 절세 전략입니다.`,
      cta: { label: '복리 시뮬레이터로 절세액 재투자 효과 확인', href: '/compound' },
    };
  }

  if (result.cumulativeBenefit > 10_000_000) {
    return {
      title: `${values.projectionYears}년간 ${formatManwon(result.cumulativeBenefit)} 절세 — 상당한 혜택입니다`,
      body: '절세액을 재투자하면 복리 효과로 실제 혜택은 더 커집니다. 이 금액을 복리 시뮬레이터에서 확인해 보세요.',
      cta: { label: '복리 시뮬레이터로 재투자 효과 확인', href: '/compound' },
    };
  }

  return {
    title: '절세는 가장 확실한 수익입니다',
    body: '시장 수익률은 불확실하지만 세제혜택은 법으로 보장된 확정 수익입니다. 매년 이 혜택을 최대한 활용하세요.',
    cta: { label: '노후 자금 목표 계산기로 전체 계획 확인', href: '/retirement/goal' },
  };
}
